import { useEffect, useRef } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

/**
 * OpenStreetMap tiles via Leaflet — no API key, unlike every hosted
 * alternative (Google, Mapbox, HERE). That trade is deliberate: naming a paid
 * maps vendor is a procurement decision for the spec owner, the same as the
 * Mobile Money and SMS providers, and this ships a working map without
 * waiting on it.
 */
export type MapViewProps = {
  center: { lat: number; lon: number }
  zoom?: number
  className?: string
  /** Fires once the user stops panning, with the new centre. */
  onMoveEnd?: (center: { lat: number; lon: number }) => void
  /** A fixed pin drawn over the map centre rather than a real Leaflet marker — see `LocationPicker`. */
  showCenterPin?: boolean
  /**
   * Points to drop a marker on, each with the label a screen reader is given.
   *
   * Real Leaflet markers, unlike `showCenterPin`: these move with the map
   * because they are places on it, where the centre pin is a viewfinder that
   * stays put while the map moves under it.
   */
  markers?: { lat: number; lon: number; label: string; tone?: 'brand' | 'danger' | 'slate' }[]
  /** A line through where something has been — the tracking trail. */
  trail?: { lat: number; lon: number }[]
}

const TONE = { brand: '#125EEA', danger: '#e11d48', slate: '#64748b' } as const

/**
 * A dot rather than Leaflet's default pin image.
 *
 * Leaflet's default marker loads its icon from a bundler-relative URL that
 * breaks under Vite, and every workaround for that ships an image for what is
 * a coloured circle. This also rotates nothing and needs no asset.
 */
function dot(colour: string): L.DivIcon {
  return L.divIcon({
    className: '',
    html:
      `<span style="display:block;width:18px;height:18px;border-radius:9999px;` +
      `background:${colour};border:3px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4)"></span>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  })
}

const TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
const ATTRIBUTION =
  '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'

export function MapView({
  center,
  zoom = 15,
  className,
  onMoveEnd,
  showCenterPin,
  markers,
  trail,
}: MapViewProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<L.Map | null>(null)
  const onMoveEndRef = useRef(onMoveEnd)
  const drawnRef = useRef<L.LayerGroup | null>(null)

  useEffect(() => {
    onMoveEndRef.current = onMoveEnd
  })

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const map = L.map(el, { center: [center.lat, center.lon], zoom, attributionControl: true })
    L.tileLayer(TILE_URL, { attribution: ATTRIBUTION, maxZoom: 19 }).addTo(map)
    map.on('moveend', () => {
      const c = map.getCenter()
      onMoveEndRef.current?.({ lat: c.lat, lon: c.lng })
    })
    mapRef.current = map

    return () => {
      map.remove()
      mapRef.current = null
    }
    // Only on mount: `center`/`zoom` after that are handled by the effect
    // below via `setView`, so the map itself is not torn down on every pan.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Redrawn wholesale on every change rather than diffed. A trail is at most a
  // couple of hundred points and a mission has two or three markers; tracking
  // which Leaflet layer corresponds to which coordinate would be more code and
  // more ways to leak a layer than clearing the group.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    drawnRef.current?.remove()
    const group = L.layerGroup().addTo(map)
    drawnRef.current = group

    if (trail && trail.length > 1) {
      L.polyline(
        trail.map((p) => [p.lat, p.lon] as [number, number]),
        { color: TONE.brand, weight: 4, opacity: 0.6 },
      ).addTo(group)
    }
    markers?.forEach((m) => {
      L.marker([m.lat, m.lon], {
        icon: dot(TONE[m.tone ?? 'brand']),
        alt: m.label,
        title: m.label,
      }).addTo(group)
    })

    return () => {
      group.remove()
      drawnRef.current = null
    }
  }, [markers, trail])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const current = map.getCenter()
    // Avoid re-centring on the pan that just produced this very coordinate.
    if (Math.abs(current.lat - center.lat) > 1e-6 || Math.abs(current.lng - center.lon) > 1e-6) {
      map.setView([center.lat, center.lon], map.getZoom())
    }
  }, [center.lat, center.lon])

  return (
    <div className={className} style={{ position: 'relative' }}>
      <div ref={containerRef} style={{ height: '100%', width: '100%' }} />
      {showCenterPin ? (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -100%)',
            pointerEvents: 'none',
            zIndex: 1000,
          }}
        >
          <svg width="32" height="42" viewBox="0 0 32 42" fill="none">
            <path
              d="M16 0C7.163 0 0 7.163 0 16c0 11 16 26 16 26s16-15 16-26C32 7.163 24.837 0 16 0Z"
              fill="#125EEA"
            />
            <circle cx="16" cy="16" r="6" fill="white" />
          </svg>
        </div>
      ) : null}
    </div>
  )
}
